import React, { useState, useEffect } from "react";
import logo from "../assets/tgci.png";
import styled from "styled-components";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";
import {
  ArrowRight,
  Call,
  LocationOn,
  Mail,
  Close,
  CheckCircle,
} from "@mui/icons-material";
import { Hamburger } from "../utils/Hamburger";
import { motion, AnimatePresence } from "framer-motion";
import "./Header.css";

/* ---------- Styled Link ---------- */
const Link = styled.a`
  font-family: Lato;
  text-transform: uppercase;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  position: relative;
  transition: color 0.3s ease;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(to right, #ff7a18, #ff9f43);
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

/* ---------- Modal Style ---------- */
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: 440,
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  p: 0,
  overflow: "hidden",
};

export default function Header() {
  const [showNav, setShowNav] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    course: "General Inquiry",
  });

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle form input
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle form submit
  const handleSubmit = () => {
    // Validate form
    if (!formData.name || !formData.email || !formData.phone) {
      alert("Please fill all required fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Please enter a valid email address");
      return;
    }

    // Phone validation
    if (formData.phone.length < 10) {
      alert("Please enter a valid phone number");
      return;
    }

    // Construct WhatsApp message
    const whatsappMessage = `🎓 *New Registration from TGCI Website*

👤 *Name:* ${formData.name}
📧 *Email:* ${formData.email}
📱 *Phone:* ${formData.phone}
📚 *Interested In:* ${formData.course}

_Registration Form - Please contact soon!_`;

    // Open WhatsApp
    const whatsappURL = `https://wa.me/917533839843?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappURL, "_blank");

    // Show success message
    setSubmitStatus("success");
    
    // Reset form after 2 seconds
    setTimeout(() => {
      setFormData({ name: "", email: "", phone: "", course: "General Inquiry" });
      setSubmitStatus(null);
      setOpenModal(false);
    }, 2000);
  };

  const courseOptions = [
    "General Inquiry",
    "DCA (Diploma in Computer Applications)",
    "ADCA (Advanced DCA)",
    "MS Office",
    "Tally Prime",
    "Graphics Designing",
    "Web Development",
    "Programming",
    "Other"
  ];

  return (
    <div className="relative z-50 lg:h-[124px] h-[82px]">
      <div className="fixed w-full">

        {/* ================= TOP INFO BAR ================= */}
        <nav className="hidden lg:flex justify-between items-center bg-gradient-to-r from-[#ff7a18] to-[#ff9f43] text-white px-8 py-2.5 text-sm shadow-md">
          <div className="flex gap-8 items-center">
            <a 
              href="mailto:tgci.ac.in@gmail.com"
              className="flex items-center gap-2 hover:text-orange-100 transition-colors"
            >
              <Mail fontSize="small" /> 
              <span>tgci.ac.in@gmail.com</span>
            </a>
            <a 
              href="tel:+917533839843"
              className="flex items-center gap-2 hover:text-orange-100 transition-colors"
            >
              <Call fontSize="small" /> 
              <span>+91 7533839843</span>
            </a>
          </div>
          <a 
            href="/contact"
            className="flex items-center gap-2 hover:text-orange-100 transition-all hover:gap-3"
          >
            <LocationOn fontSize="small" />
            <span className="font-semibold">Visit Us</span>
            <ArrowRight fontSize="small" />
          </a>
        </nav>

        {/* ================= MAIN NAVBAR ================= */}
        <nav 
          className={`bg-white w-full flex items-center justify-between px-6 lg:px-8 py-4 transition-all duration-300 ${
            scrolled ? "shadow-xl" : "shadow-lg"
          }`}
        >
          
          {/* Logo */}
          <a href="/" className="flex gap-4 items-center group">
            <div className="relative">
              <img
                src={logo}
                alt="TGCI Logo"
                className="lg:h-[70px] lg:w-[70px] w-[50px] h-[50px] transition-transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-orange-400 rounded-full opacity-0 group-hover:opacity-10 transition-opacity"></div>
            </div>
            <div>
              <span className="font-bold text-xl lg:text-2xl font-[Raleway] text-gray-900 group-hover:text-[#ff7a18] transition-colors">
                TGCI
              </span>
              <span className="block text-xs text-gray-500 font-medium">
                We Promote Learning
              </span>
            </div>
          </a>

          {/* Desktop Menu */}
          <div className="hidden lg:flex gap-8 items-center">
            <Link href="/" className="text-gray-800 hover:text-[#ff7a18]">
              Home
            </Link>
            <Link href="/about" className="text-gray-800 hover:text-[#ff7a18]">
              About
            </Link>
            <Link href="/courses" className="text-gray-800 hover:text-[#ff7a18]">
              Courses
            </Link>
            <Link href="/services" className="text-gray-800 hover:text-[#ff7a18]">
              Services
            </Link>
            <Link href="/certificate" className="text-gray-800 hover:text-[#ff7a18]">
              Certificate
            </Link>
            <Link href="/contact" className="text-gray-800 hover:text-[#ff7a18]">
              Contact
            </Link>

            {/* Register Button */}
            <button
              onClick={() => setOpenModal(true)}
              className="bg-gradient-to-r from-[#ff7a18] to-[#ff9f43] text-white px-6 py-2.5 rounded-full font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              Register Now 
              <ArrowRight fontSize="small" />
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="lg:hidden flex items-center gap-4">
            <button
              onClick={() => setOpenModal(true)}
              className="bg-gradient-to-r from-[#ff7a18] to-[#ff9f43] text-white px-4 py-2 rounded-full font-semibold shadow-md text-sm"
            >
              Register
            </button>
            <Hamburger showNav={showNav} setShowNav={setShowNav} />
          </div>
        </nav>

        {/* ================= MOBILE DROPDOWN ================= */}
        <AnimatePresence>
          {showNav && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden absolute top-[82px] w-full bg-white shadow-2xl overflow-hidden"
            >
              <div className="flex flex-col items-center gap-6 py-8">
                <Link href="/" className="text-gray-800 hover:text-[#ff7a18]">
                  Home
                </Link>
                <Link href="/about" className="text-gray-800 hover:text-[#ff7a18]">
                  About
                </Link>
                <Link href="/courses" className="text-gray-800 hover:text-[#ff7a18]">
                  Courses
                </Link>
                <Link href="/services" className="text-gray-800 hover:text-[#ff7a18]">
                  Services
                </Link>
                <Link href="/certificate" className="text-gray-800 hover:text-[#ff7a18]">
                  Certificate
                </Link>
                <Link href="/contact" className="text-gray-800 hover:text-[#ff7a18]">
                  Contact
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ================= REGISTER MODAL ================= */}
      <Modal 
        open={openModal} 
        onClose={() => {
          setOpenModal(false);
          setSubmitStatus(null);
        }}
        closeAfterTransition
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <Box sx={modalStyle}>
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#ff7a18] to-[#ff9f43] p-6 relative">
              <button
                onClick={() => {
                  setOpenModal(false);
                  setSubmitStatus(null);
                }}
                className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-1 transition-colors"
              >
                <Close />
              </button>
              
              <Typography variant="h5" fontWeight="bold" color="white" className="mb-2">
                Register Now
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.9)">
                Start your learning journey with TGCI today!
              </Typography>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {submitStatus === "success" ? (
                <div className="text-center py-8">
                  <CheckCircle sx={{ fontSize: 64, color: "#4caf50", mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" color="text.primary" className="mb-2">
                    Registration Submitted!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    WhatsApp is opening. We'll contact you soon!
                  </Typography>
                </div>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" className="mb-4">
                    Fill in your details and our team will contact you shortly to guide you through our courses.
                  </Typography>

                  <TextField 
                    fullWidth 
                    label="Full Name" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                    required
                  />
                  <TextField 
                    fullWidth 
                    label="Email Address" 
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                    required
                  />
                  <TextField 
                    fullWidth 
                    label="Phone Number" 
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                    required
                  />
                  <TextField
                    fullWidth
                    select
                    label="Course Interest"
                    name="course"
                    value={formData.course}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                  >
                    {courseOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    sx={{
                      mt: 3,
                      py: 1.5,
                      background: "linear-gradient(to right, #ff7a18, #ff9f43)",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "16px",
                      borderRadius: "8px",
                      textTransform: "none",
                      boxShadow: "0 4px 12px rgba(255, 122, 24, 0.3)",
                      ":hover": { 
                        background: "linear-gradient(to right, #ff6a08, #ff8f33)",
                        boxShadow: "0 6px 16px rgba(255, 122, 24, 0.4)",
                      },
                    }}
                  >
                    Submit Registration
                  </Button>

                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    className="block text-center mt-4"
                  >
                    By registering, you agree to our Terms & Conditions
                  </Typography>
                </>
              )}
            </div>

          </Box>
        </motion.div>
      </Modal>
    </div>
  );
}